<template>
  <div class="container-projeto">
    <div class="detalhe-projeto" v-if="projeto">
      <!-- Breadcrumb -->
      <nav class="breadcrumb">
        <RouterLink
          :to="{ name: 'gestao-projetos.projetos' }"
          class="breadcrumb-link"
        >
          ← Voltar para Projetos
        </RouterLink>
      </nav>

      <!-- Header Principal -->
      <header class="cabecalho-principal">
        <div class="info-projeto">
          <div
            class="badge-situacao"
            :class="`situacao-${projeto.situacao
              ?.toLowerCase()
              ?.replace(/\s+/g, '-')}`"
          >
            {{ projeto.situacao }}
          </div>
          <h1 class="titulo-projeto">{{ projeto.titulo }}</h1>
          <p class="descricao-projeto">
            {{ projeto.descricao || "Projeto sem descrição detalhada." }}
          </p>

          <!-- Indicadores rápidos -->
          <div class="indicadores-rapidos">
            <div class="indicador">
              <span class="indicador-label">Saúde</span>
              <span
                class="indicador-valor saude"
                :class="`saude-${projeto.nivelSaude
                  ?.toLowerCase()
                  ?.replace(/\s+/g, '-')}`"
              >
                {{ projeto.nivelSaude }}
              </span>
            </div>
            <div class="indicador">
              <span class="indicador-label">Localização</span>
              <span class="indicador-valor"
                >{{ projeto.siglaEstado || "—" }} /
                {{ projeto.nomeMunicipio || "—" }}</span
              >
            </div>
            <div class="indicador">
              <span class="indicador-label">Duração</span>
              <span class="indicador-valor">{{
                formatarPeriodo(projeto.dataInicio, projeto.dataFim)
              }}</span>
            </div>
          </div>
        </div>

        <div class="acoes-principais">
          <button
            type="button"
            class="botao-acao botao-mensurar"
            @click="irParaMensuracao"
          >
            📊 Mensurar
          </button>
          <button
            type="button"
            class="botao-acao botao-editar"
            @click="irParaEdicao()"
          >
            ✏️ Editar
          </button>
          <div class="dropdown-menu">
            <button type="button" class="botao-acao botao-menu">⋮ Mais</button>
            <div class="dropdown-content">
              <button
                type="button"
                class="dropdown-item botao-excluir"
                :disabled="processandoExclusao"
                @click="abrirExclusao"
              >
                🗑️ Excluir Projeto
              </button>
            </div>
          </div>
        </div>
      </header>

      <!-- Cards de Ações Rápidas -->
      <section class="acoes-rapidas">
        <div class="grid-acoes">
          <button
            type="button"
            class="acao-rapida"
            @click="irParaEdicao('acoes')"
          >
            <div class="icone-acao nova-acao">➕</div>
            <div class="texto-acao">
              <strong>Nova Ação</strong>
              <span>Cadastre uma nova frente de trabalho</span>
            </div>
          </button>
          <button
            type="button"
            class="acao-rapida"
            @click="irParaEdicao('acoes')"
          >
            <div class="icone-acao registrar-entrega">✅</div>
            <div class="texto-acao">
              <strong>Registrar Entrega</strong>
              <span>Inclua atividades concluídas</span>
            </div>
          </button>
          <button
            type="button"
            class="acao-rapida"
            @click="irParaEdicao('metricas')"
          >
            <div class="icone-acao adicionar-metrica">📈</div>
            <div class="texto-acao">
              <strong>Adicionar Métrica</strong>
              <span>Monitore indicadores chave</span>
            </div>
          </button>
        </div>
      </section>

      <!-- Resumo Executivo -->
      <section class="resumo-executivo">
        <h2>📋 Resumo Executivo</h2>
        <div class="grid-resumo">
          <div class="card-resumo destaque">
            <div class="card-header">
              <h3>💰 Orçamento</h3>
            </div>
            <div class="orcamento-valores">
              <div class="valor-planejado">
                <span class="label">Planejado</span>
                <span class="valor">{{
                  formatarValor(projeto.orcamentoPlanejado)
                }}</span>
              </div>
              <div class="valor-executado">
                <span class="label">Executado</span>
                <span class="valor">{{
                  formatarValor(projeto.orcamentoExecutado)
                }}</span>
              </div>
              <div class="progresso-orcamento">
                <div class="barra-progresso">
                  <div
                    class="progresso-fill"
                    :style="{ width: calcularProgressoOrcamento() + '%' }"
                  ></div>
                </div>
                <span class="percentual"
                  >{{ calcularProgressoOrcamento() }}%</span
                >
              </div>
            </div>
          </div>

          <div class="card-resumo">
            <div class="card-header">
              <h3>🎯 Plano Municipal</h3>
            </div>
            <div class="card-content">
              <strong>{{ nomePlanoMunicipal() }}</strong>
              <div class="eixos-tags">
                <span
                  v-for="eixo in eixosResumo.slice(0, 3)"
                  :key="eixo"
                  class="tag-eixo"
                >
                  {{ eixo }}
                </span>
                <span v-if="eixosResumo.length > 3" class="tag-mais">
                  +{{ eixosResumo.length - 3 }} mais
                </span>
                <span v-if="!eixosResumo.length" class="sem-eixos">
                  Nenhuma área chave vinculada
                </span>
              </div>
            </div>
          </div>

          <div class="card-resumo">
            <div class="card-header">
              <h3>⚡ Progresso</h3>
            </div>
            <div class="card-content progresso-geral">
              <div class="estatisticas-rapidas">
                <div class="stat">
                  <span class="numero">{{ acoes.length }}</span>
                  <span class="label">Ações</span>
                </div>
                <div class="stat">
                  <span class="numero">{{ totalEntregas }}</span>
                  <span class="label">Entregas</span>
                </div>
                <div class="stat">
                  <span class="numero">{{ projeto.equipes?.length || 0 }}</span>
                  <span class="label">Membros</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Conteúdo Principal em Tabs -->
      <section class="conteudo-principal">
        <div class="tabs-container">
          <nav class="tabs-nav">
            <button
              type="button"
              class="tab-button"
              :class="{ active: tabAtiva === 'acoes' }"
              @click="tabAtiva = 'acoes'"
            >
              🎯 Ações ({{ acoes.length }})
            </button>
            <button
              type="button"
              class="tab-button"
              :class="{ active: tabAtiva === 'equipe' }"
              @click="tabAtiva = 'equipe'"
            >
              👥 Equipe ({{ projeto.equipes?.length || 0 }})
            </button>
            <button
              type="button"
              class="tab-button"
              :class="{ active: tabAtiva === 'orcamento' }"
              @click="tabAtiva = 'orcamento'"
            >
              💰 Orçamentos ({{ projeto.orcamentos?.length || 0 }})
            </button>
            <button
              type="button"
              class="tab-button"
              :class="{ active: tabAtiva === 'metricas' }"
              @click="tabAtiva = 'metricas'"
            >
              📊 Métricas ({{ projeto.metricas?.length || 0 }})
            </button>
          </nav>

          <!-- Tab Ações -->
          <div v-show="tabAtiva === 'acoes'" class="tab-content">
            <div class="secao-header">
              <h3>🎯 Ações do Projeto</h3>
              <button
                type="button"
                class="botao-adicionar"
                @click="irParaEdicao('acoes')"
              >
                ➕ Nova Ação
              </button>
            </div>

            <div v-if="!acoes.length" class="estado-vazio">
              <div class="vazio-icone">🎯</div>
              <h4>Nenhuma ação cadastrada</h4>
              <p>Comece criando a primeira ação do projeto</p>
              <button
                type="button"
                class="botao-primario"
                @click="irParaEdicao('acoes')"
              >
                Criar primeira ação
              </button>
            </div>

            <div v-else class="grid-cards">
              <article
                v-for="acao in acoes"
                :key="acao.idAcao || acao.idMarco || acao.__id"
                class="card-acao"
              >
                <header class="card-acao-header">
                  <h4>{{ acao.titulo }}</h4>
                  <span class="periodo">{{
                    formatarPeriodo(
                      acao.dataInicio || acao.dataLimite,
                      acao.dataFim || acao.dataLimite
                    )
                  }}</span>
                </header>

                <p class="descricao">{{ acao.descricao || "Sem descrição" }}</p>

                <div v-if="metricasDaAcao(acao).length" class="metricas-acao">
                  <span class="metricas-label">Métricas:</span>
                  <div class="tags-metricas">
                    <span
                      v-for="metrica in metricasDaAcao(acao)"
                      :key="metrica.idMetrica || metrica.__id || metrica.nome"
                      class="tag-metrica"
                    >
                      {{ metrica.nome || "Métrica sem nome" }}
                    </span>
                  </div>
                </div>

                <div v-if="entregasDaAcao(acao).length" class="entregas-acao">
                  <span class="entregas-label">Entregas:</span>
                  <ul class="lista-entregas">
                    <li
                      v-for="entrega in entregasDaAcao(acao)"
                      :key="entrega.idEntrega || entrega.__id"
                      class="item-entrega"
                    >
                      <div class="entrega-info">
                        <strong>{{ entrega.titulo }}</strong>
                        <small>{{
                          entrega.nomeResponsavel || "Sem responsável"
                        }}</small>
                      </div>
                      <span
                        class="entrega-situacao"
                        :class="`situacao-${entrega.situacao
                          ?.toLowerCase()
                          ?.replace(/\s+/g, '-')}`"
                      >
                        {{ entrega.situacao || "—" }}
                      </span>
                    </li>
                  </ul>
                </div>

                <div v-if="!entregasDaAcao(acao).length" class="sem-entregas">
                  <small>Nenhuma entrega registrada</small>
                </div>
              </article>
            </div>
          </div>
          <!-- Tab Equipe -->
          <div v-show="tabAtiva === 'equipe'" class="tab-content">
            <div class="secao-header">
              <h3>👥 Equipe do Projeto</h3>
              <button
                type="button"
                class="botao-adicionar"
                @click="irParaEdicao('equipe')"
              >
                👤 Gerir Equipe
              </button>
            </div>

            <div v-if="!projeto.equipes.length" class="estado-vazio">
              <div class="vazio-icone">👥</div>
              <h4>Nenhum membro na equipe</h4>
              <p>Adicione membros para organizar o trabalho</p>
              <button
                type="button"
                class="botao-primario"
                @click="irParaEdicao('equipe')"
              >
                Adicionar membro
              </button>
            </div>

            <div v-else class="grid-cards">
              <article
                v-for="membro in projeto.equipes"
                :key="membro.idMembro"
                class="card-membro"
              >
                <div class="membro-header">
                  <div class="membro-avatar">
                    {{ (membro.nomeUsuario || "U")[0].toUpperCase() }}
                  </div>
                  <div class="membro-info">
                    <h4>{{ membro.nomeUsuario || "Nome não informado" }}</h4>
                    <span class="papel">{{
                      membro.papel || "Papel não informado"
                    }}</span>
                  </div>
                  <div class="alocacao">
                    <span class="alocacao-valor"
                      >{{ membro.alocacaoPercentual || 0 }}%</span
                    >
                    <span class="alocacao-label">Alocação</span>
                  </div>
                </div>

                <div v-if="membro.emailUsuario" class="membro-contato">
                  <span class="contato-icone">📧</span>
                  <span class="contato-email">{{ membro.emailUsuario }}</span>
                </div>

                <div class="membro-responsabilidades">
                  <p>
                    {{
                      membro.responsabilidades ||
                      "Sem responsabilidades registradas."
                    }}
                  </p>
                </div>
              </article>
            </div>
          </div>

          <!-- Tab Orçamentos -->
          <div v-show="tabAtiva === 'orcamento'" class="tab-content">
            <div class="secao-header">
              <h3>💰 Orçamentos do Projeto</h3>
              <button
                type="button"
                class="botao-adicionar"
                @click="irParaEdicao('orcamento')"
              >
                💰 Novo Registro
              </button>
            </div>

            <div v-if="!projeto.orcamentos.length" class="estado-vazio">
              <div class="vazio-icone">💰</div>
              <h4>Nenhum lançamento cadastrado</h4>
              <p>Registre receitas e despesas do projeto</p>
              <button
                type="button"
                class="botao-primario"
                @click="irParaEdicao('orcamento')"
              >
                Adicionar lançamento
              </button>
            </div>

            <div v-else class="grid-cards">
              <article
                v-for="orcamento in projeto.orcamentos"
                :key="orcamento.idOrcamento"
                class="card-orcamento"
              >
                <header class="orcamento-header">
                  <h4>
                    {{ orcamento.categoria || "Categoria não informada" }}
                  </h4>
                  <span class="orcamento-tipo">{{
                    orcamento.tipo || "Geral"
                  }}</span>
                </header>

                <div class="valores-orcamento">
                  <div class="valor-item">
                    <span class="valor-label">Planejado</span>
                    <span class="valor-quantia planejado">{{
                      formatarValor(orcamento.valorPlanejado)
                    }}</span>
                  </div>
                  <div class="valor-item">
                    <span class="valor-label">Executado</span>
                    <span class="valor-quantia executado">{{
                      formatarValor(orcamento.valorExecutado)
                    }}</span>
                  </div>
                  <div class="progresso-orcamento-item">
                    <div class="barra-progresso-pequena">
                      <div
                        class="progresso-fill-pequena"
                        :style="{
                          width:
                            calcularProgressoOrcamentoItem(orcamento) + '%',
                        }"
                      ></div>
                    </div>
                    <span class="percentual-pequeno"
                      >{{ calcularProgressoOrcamentoItem(orcamento) }}%</span
                    >
                  </div>
                </div>
              </article>
            </div>
          </div>

          <!-- Tab Métricas -->
          <div v-show="tabAtiva === 'metricas'" class="tab-content">
            <div class="secao-header">
              <h3>📊 Métricas do Projeto</h3>
              <button
                type="button"
                class="botao-adicionar"
                @click="irParaEdicao('metricas')"
              >
                📊 Nova Métrica
              </button>
            </div>

            <div v-if="!projeto.metricas.length" class="estado-vazio">
              <div class="vazio-icone">📊</div>
              <h4>Nenhuma métrica cadastrada</h4>
              <p>Monitore o progresso com indicadores</p>
              <button
                type="button"
                class="botao-primario"
                @click="irParaEdicao('metricas')"
              >
                Criar primeira métrica
              </button>
            </div>

            <div v-else class="grid-cards">
              <article
                v-for="metrica in projeto.metricas"
                :key="metrica.idMetrica"
                class="card-metrica"
              >
                <header class="metrica-header">
                  <h4>{{ metrica.nome }}</h4>
                  <span class="metrica-tipo">{{ metrica.tipo }}</span>
                </header>

                <div class="metrica-valores">
                  <div class="valor-meta">
                    <span class="label">Meta</span>
                    <span class="valor">{{ metrica.meta || "—" }}</span>
                  </div>
                  <div class="valor-atual">
                    <span class="label">Atual</span>
                    <span class="valor">{{ metrica.valorAtual || "—" }}</span>
                  </div>

                  <div
                    v-if="metrica.meta && metrica.valorAtual"
                    class="progresso-metrica"
                  >
                    <div class="barra-progresso-pequena">
                      <div
                        class="progresso-fill-pequena metrica"
                        :style="{
                          width: calcularProgressoMetrica(metrica) + '%',
                        }"
                      ></div>
                    </div>
                    <span class="percentual-pequeno"
                      >{{ calcularProgressoMetrica(metrica) }}%</span
                    >
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>
    </div>
    <div v-else-if="carregando" class="carregando">
      <div class="vazio-icone">⏳</div>
      <p>Carregando projeto...</p>
    </div>
    <div v-else class="erro">
      {{ erro || "Projeto não encontrado." }}
    </div>

    <!-- Modal de Exclusão -->
    <div
      v-if="exibindoExclusao"
      class="modal-overlay"
      @click.self="fecharExclusao"
    >
      <div class="modal-caixa">
        <h3>🗑️ Excluir projeto</h3>
        <p class="modal-descricao">
          Confirme a exclusão do projeto selecionado. Esta ação não poderá ser
          desfeita e todos os dados relacionados serão perdidos permanentemente.
        </p>
        <div class="grupo-botoes">
          <button type="button" class="botao-texto" @click="fecharExclusao">
            Cancelar
          </button>
          <button
            type="button"
            class="botao-primario perigoso"
            :disabled="processandoExclusao"
            @click="confirmarExclusao"
          >
            {{ processandoExclusao ? "Excluindo..." : "Confirmar exclusão" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  buscarPdmDetalhe,
  buscarProjeto,
  excluirProjeto,
} from "../services/api";

export default {
  name: "DetalheGestaoProjeto",
  data() {
    return {
      projeto: null,
      pdmDetalhe: null,
      erro: "",
      carregando: false,
      exibindoExclusao: false,
      processandoExclusao: false,
      tabAtiva: "acoes",
    };
  },
  computed: {
    acoes() {
      if (!this.projeto) return [];
      if (Array.isArray(this.projeto.acoes) && this.projeto.acoes.length) {
        return this.projeto.acoes;
      }
      return this.projeto.marcos || [];
    },
    eixosResumo() {
      return this.eixosDoProjeto();
    },
    totalEntregas() {
      return this.acoes.reduce((total, acao) => {
        return total + this.entregasDaAcao(acao).length;
      }, 0);
    },
  },
  async created() {
    await this.carregarProjeto();
    this.sincronizarRota();
  },
  watch: {
    "$route.params.idProjeto": "carregarProjeto",
    "$route.name": {
      immediate: true,
      handler() {
        this.sincronizarRota();
      },
    },
  },
  methods: {
    async carregarProjeto() {
      try {
        this.carregando = true;
        const { idProjeto } = this.$route.params;
        const { data } = await buscarProjeto(idProjeto);
        this.projeto = data;
        await this.carregarPdmDoProjeto(data);
        this.erro = "";
      } catch (error) {
        this.erro = "Não foi possível carregar o projeto.";
        this.$toasted && this.$toasted.error(this.erro);
        this.projeto = null;
        this.pdmDetalhe = null;
        // eslint-disable-next-line no-console
        console.error("[gestao-projetos] detalhe", error);
      } finally {
        this.carregando = false;
      }
    },
    async carregarPdmDoProjeto(projeto) {
      this.pdmDetalhe = null;
      if (!projeto || !projeto.idPdm) return;
      try {
        const { data } = await buscarPdmDetalhe(projeto.idPdm);
        this.pdmDetalhe = data;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("[gestao-projetos] pdm do projeto", error);
      }
    },
    sincronizarRota() {
      this.exibindoExclusao = this.$route.name === "gestao-projetos.excluir";
    },
    entregasDaAcao(acao) {
      if (!acao) return [];
      if (Array.isArray(acao.entregas) && acao.entregas.length) {
        return acao.entregas;
      }
      const idAcao = acao.idAcao || acao.idMarco;
      return (this.projeto.entregas || []).filter(
        (entrega) => entrega.idAcao === idAcao || entrega.idMarco === idAcao
      );
    },
    metricasDaAcao(acao) {
      if (!acao) return [];
      const metricasProjeto = Array.isArray(this.projeto.metricas)
        ? this.projeto.metricas
        : [];
      const coletadas = [];
      const inserir = (metrica) => {
        if (!metrica) return;
        const chave =
          metrica.idMetrica || metrica.__id || metrica.id || metrica.nome;
        if (
          !chave ||
          coletadas.some(
            (item) =>
              (item.idMetrica || item.__id || item.id || item.nome) === chave
          )
        )
          return;
        coletadas.push(metrica);
      };
      if (Array.isArray(acao.metricas)) {
        acao.metricas.forEach((metrica) => {
          if (metrica && typeof metrica === "object") {
            inserir(metrica);
          } else {
            const encontrada = metricasProjeto.find(
              (item) => item.idMetrica === metrica
            );
            inserir(encontrada);
          }
        });
      }
      if (Array.isArray(acao.metricasIds)) {
        acao.metricasIds.forEach((id) =>
          inserir(metricasProjeto.find((metrica) => metrica.idMetrica === id))
        );
      }
      if (Array.isArray(acao.metricasVinculadas)) {
        acao.metricasVinculadas.forEach((referencia) => {
          inserir(
            metricasProjeto.find(
              (metrica) =>
                metrica.idMetrica === referencia || metrica.__id === referencia
            )
          );
        });
      }
      return coletadas;
    },
    formatarData(valor) {
      if (!valor) return "—";
      const data = new Date(valor);
      if (Number.isNaN(data.getTime())) return "—";
      return data.toLocaleDateString("pt-BR");
    },
    nomePlanoMunicipal() {
      if (!this.projeto) return "—";
      return (
        this.projeto.nomePdm ||
        this.projeto.nomePlanoMunicipal ||
        this.projeto.planoMunicipal?.nome ||
        this.projeto.pdm?.nome ||
        this.projeto.pdmNome ||
        "—"
      );
    },
    eixosDoProjeto() {
      if (!this.projeto) return [];
      const encontrados = new Set();
      const adicionar = (valor) => {
        if (!valor) return;
        const texto = String(valor).trim();
        if (!texto) return;
        encontrados.add(texto);
      };
      const listas = [
        this.projeto.eixosPdm,
        this.projeto.eixos,
        this.projeto.eixosRelacionados,
        this.projeto.eixosVinculados,
        this.projeto.eixosProjeto,
      ];
      listas.forEach((lista) => {
        if (Array.isArray(lista)) {
          lista.forEach((item) => {
            if (typeof item === "string") {
              adicionar(item);
            } else if (item && typeof item === "object") {
              adicionar(item.nome || item.descricao || item.titulo);
            }
          });
        }
      });
      if (Array.isArray(this.projeto.idsEixosPdm) && this.pdmDetalhe) {
        const ids = new Set(this.projeto.idsEixosPdm.map((id) => Number(id)));
        (this.pdmDetalhe.eixos || []).forEach((eixo) => {
          if (ids.has(Number(eixo.idEixo))) {
            adicionar(eixo.nome);
          }
        });
      }
      adicionar(this.projeto.eixoPdm);
      return Array.from(encontrados);
    },
    formatarPeriodo(inicio, fim) {
      if (!inicio && !fim) return "—";
      const inicioFmt = this.formatarData(inicio);
      const fimFmt = this.formatarData(fim);
      if (inicioFmt === "—") return fimFmt;
      if (fimFmt === "—") return inicioFmt;
      return `${inicioFmt} - ${fimFmt}`;
    },
    formatarValor(valor) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(valor || 0);
    },
    irParaEdicao(aba) {
      this.$router.push({
        name: "gestao-projetos.editar",
        params: { idProjeto: this.projeto.idProjeto },
        query: { aba },
      });
    },
    irParaMensuracao() {
      this.$router.push({
        name: "gestao-projetos.mensuracao",
        params: { idProjeto: this.projeto.idProjeto },
      });
    },
    calcularProgressoOrcamento() {
      if (
        !this.projeto.orcamentoPlanejado ||
        this.projeto.orcamentoPlanejado === 0
      )
        return 0;
      const progresso =
        (this.projeto.orcamentoExecutado / this.projeto.orcamentoPlanejado) *
        100;
      return Math.min(Math.round(progresso), 100);
    },
    calcularProgressoOrcamentoItem(orcamento) {
      if (!orcamento.valorPlanejado || orcamento.valorPlanejado === 0) return 0;
      const progresso =
        (orcamento.valorExecutado / orcamento.valorPlanejado) * 100;
      return Math.min(Math.round(progresso), 100);
    },
    calcularProgressoMetrica(metrica) {
      if (!metrica.meta || metrica.meta === 0 || !metrica.valorAtual) return 0;
      const progresso = (metrica.valorAtual / metrica.meta) * 100;
      return Math.min(Math.round(progresso), 100);
    },
    abrirExclusao() {
      if (!this.projeto) return;
      this.exibindoExclusao = true;
      if (this.$route.name !== "gestao-projetos.excluir") {
        const destino = {
          name: "gestao-projetos.excluir",
          params: { idProjeto: this.projeto.idProjeto },
        };
        this.$router.push(destino).catch((erro) => {
          if (!erro || erro.name !== "NavigationDuplicated") {
            throw erro;
          }
        });
      }
    },
    fecharExclusao() {
      this.exibindoExclusao = false;
      if (this.$route.name === "gestao-projetos.excluir") {
        const idProjeto =
          this.$route.params.idProjeto ||
          (this.projeto && this.projeto.idProjeto);
        if (idProjeto) {
          this.$router.push({
            name: "gestao-projetos.detalhe",
            params: { idProjeto },
          });
        }
      }
    },
    async confirmarExclusao() {
      if (!this.projeto || this.processandoExclusao) return;
      try {
        this.processandoExclusao = true;
        await excluirProjeto(this.projeto.idProjeto);
        this.$toasted && this.$toasted.success("Projeto excluído com sucesso!");
        this.projeto = null;
        this.fecharExclusao();
        this.$router.push({ name: "gestao-projetos.projetos" });
      } catch (error) {
        this.$toasted &&
          this.$toasted.error("Não foi possível excluir o projeto.");
        // eslint-disable-next-line no-console
        console.error("[gestao-projetos] excluir projeto", error);
      } finally {
        this.processandoExclusao = false;
      }
    },
  },
};
</script>

<style scoped>
/* Container Principal */
.container-projeto {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  min-height: 100vh;
}

/* Breadcrumb */
.breadcrumb {
  margin-bottom: 20px;
}

.breadcrumb-link {
  color: #64748b;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.breadcrumb-link:hover {
  color: #1d4ed8;
}

/* Header Principal */
.cabecalho-principal {
  background: linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%);
  border-radius: 20px;
  padding: 30px;
  margin-bottom: 30px;
  color: white;
  box-shadow: 0 10px 30px rgba(29, 78, 216, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 30px;
}

.info-projeto {
  flex: 1;
}

.badge-situacao {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 15px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

.titulo-projeto {
  margin: 0 0 10px 0;
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1.2;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.descricao-projeto {
  font-size: 1.1rem;
  opacity: 0.9;
  line-height: 1.5;
  margin-bottom: 25px;
}

.indicadores-rapidos {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
}

.indicador {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.indicador-label {
  font-size: 0.8rem;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.indicador-valor {
  font-weight: 600;
  font-size: 1rem;
}

.saude {
  padding: 4px 8px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  text-align: center;
}

/* Ações Principais */
.acoes-principais {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.botao-acao {
  padding: 12px 20px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.3s;
  background: rgba(255, 255, 255, 0.9);
  color: #1d4ed8;
  backdrop-filter: blur(10px);
  min-width: 140px;
}

.botao-acao:hover {
  background: white;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

/* Dropdown Menu */
.dropdown-menu {
  position: relative;
  display: inline-block;
}

.dropdown-content {
  display: none;
  position: absolute;
  right: 0;
  top: 100%;
  background: white;
  min-width: 180px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  padding: 8px;
  z-index: 1000;
}

.dropdown-menu:hover .dropdown-content {
  display: block;
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.2s;
  color: #374151;
}

.dropdown-item:hover {
  background: #f3f4f6;
}

.botao-excluir {
  color: #dc2626 !important;
}

/* Ações Rápidas */
.acoes-rapidas {
  margin-bottom: 30px;
}

.grid-acoes {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.acao-rapida {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.acao-rapida:hover {
  border-color: #1d4ed8;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(29, 78, 216, 0.15);
}

.icone-acao {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  font-weight: bold;
}

.nova-acao {
  background: linear-gradient(135deg, #10b981, #059669);
}
.registrar-entrega {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}
.adicionar-metrica {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
}

.texto-acao {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.texto-acao strong {
  font-size: 1.1rem;
  color: #1f2937;
}

.texto-acao span {
  color: #6b7280;
  font-size: 0.9rem;
}

/* Resumo Executivo */
.resumo-executivo {
  margin-bottom: 40px;
}

.resumo-executivo h2 {
  font-size: 1.8rem;
  color: #1f2937;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.grid-resumo {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.card-resumo {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s;
}

.card-resumo:hover {
  transform: translateY(-4px);
}

.card-resumo.destaque {
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  border: 2px solid #10b981;
}

.card-header h3 {
  margin: 0 0 15px 0;
  font-size: 1.2rem;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
}

.orcamento-valores {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.valor-planejado,
.valor-executado {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.valor-planejado .label,
.valor-executado .label {
  color: #6b7280;
  font-weight: 500;
}

.valor-planejado .valor {
  color: #059669;
  font-weight: 700;
  font-size: 1.1rem;
}

.valor-executado .valor {
  color: #dc2626;
  font-weight: 700;
  font-size: 1.1rem;
}

.progresso-orcamento {
  display: flex;
  align-items: center;
  gap: 10px;
}

.barra-progresso {
  flex: 1;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.progresso-fill {
  height: 100%;
  background: linear-gradient(90deg, #10b981, #059669);
  border-radius: 4px;
  transition: width 0.3s;
}

.percentual {
  font-weight: 600;
  color: #059669;
  font-size: 0.9rem;
}

.eixos-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.tag-eixo {
  background: #dbeafe;
  color: #1d4ed8;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.tag-mais {
  background: #f3f4f6;
  color: #6b7280;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
}

.sem-eixos {
  color: #9ca3af;
  font-style: italic;
}

.estatisticas-rapidas {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
}

.stat {
  text-align: center;
  padding: 15px;
  background: #f8fafc;
  border-radius: 12px;
}

.stat .numero {
  display: block;
  font-size: 2rem;
  font-weight: 700;
  color: #1d4ed8;
  line-height: 1;
}

.stat .label {
  font-size: 0.8rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Tabs */
.tabs-container {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.tabs-nav {
  display: flex;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  overflow-x: auto;
}

.tab-button {
  padding: 16px 24px;
  border: none;
  background: none;
  cursor: pointer;
  font-weight: 600;
  color: #64748b;
  transition: all 0.3s;
  white-space: nowrap;
  position: relative;
}

.tab-button:hover {
  color: #1d4ed8;
  background: rgba(29, 78, 216, 0.05);
}

.tab-button.active {
  color: #1d4ed8;
  background: white;
}

.tab-button.active::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: #1d4ed8;
}

/* Tab Content */
.tab-content {
  padding: 30px;
}

.secao-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
}

.secao-header h3 {
  margin: 0;
  font-size: 1.5rem;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 10px;
}

.botao-adicionar {
  background: #1d4ed8;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.botao-adicionar:hover {
  background: #1e40af;
  transform: translateY(-1px);
}

/* Estados Vazios */
.estado-vazio {
  text-align: center;
  padding: 60px 20px;
  background: #f8fafc;
  border-radius: 12px;
  border: 2px dashed #cbd5e1;
}

.vazio-icone {
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.6;
}

.estado-vazio h4 {
  margin: 0 0 10px 0;
  color: #374151;
  font-size: 1.2rem;
}

.estado-vazio p {
  color: #6b7280;
  margin-bottom: 20px;
}

.botao-primario {
  background: #1d4ed8;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.botao-primario:hover {
  background: #1e40af;
  transform: translateY(-1px);
}

/* Grid de Cards */
.grid-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
}

/* Cards de Ação */
.card-acao {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.card-acao:hover {
  border-color: #1d4ed8;
  box-shadow: 0 8px 25px rgba(29, 78, 216, 0.1);
  transform: translateY(-2px);
}

.card-acao-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 15px;
}

.card-acao-header h4 {
  margin: 0;
  font-size: 1.1rem;
  color: #1f2937;
  line-height: 1.3;
}

.periodo {
  font-size: 0.85rem;
  color: #6b7280;
  white-space: nowrap;
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 6px;
}

.descricao {
  color: #6b7280;
  line-height: 1.5;
  margin-bottom: 15px;
}

.metricas-acao,
.entregas-acao {
  margin-bottom: 15px;
}

.metricas-label,
.entregas-label {
  font-size: 0.85rem;
  color: #374151;
  font-weight: 600;
  margin-bottom: 8px;
  display: block;
}

.tags-metricas {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tag-metrica {
  background: #dbeafe;
  color: #1d4ed8;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.lista-entregas {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.item-entrega {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 8px;
  gap: 10px;
}

.entrega-info strong {
  display: block;
  color: #1f2937;
  font-size: 0.9rem;
}

.entrega-info small {
  color: #6b7280;
  font-size: 0.8rem;
}

.entrega-situacao {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 8px;
  background: #e5e7eb;
  color: #374151;
}

.sem-entregas {
  text-align: center;
  padding: 15px;
  color: #9ca3af;
  font-style: italic;
  background: #f9fafb;
  border-radius: 8px;
}

/* Cards de Membro */
.card-membro {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s;
}

.card-membro:hover {
  border-color: #1d4ed8;
  box-shadow: 0 8px 25px rgba(29, 78, 216, 0.1);
}

.membro-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
}

.membro-avatar {
  width: 50px;
  height: 50px;
  border-radius: 25px;
  background: linear-gradient(135deg, #1d4ed8, #2563eb);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.2rem;
}

.membro-info {
  flex: 1;
}

.membro-info h4 {
  margin: 0 0 4px 0;
  color: #1f2937;
  font-size: 1.1rem;
}

.papel {
  color: #6b7280;
  font-size: 0.9rem;
}

.alocacao {
  text-align: right;
}

.alocacao-valor {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1d4ed8;
  display: block;
}

.alocacao-label {
  font-size: 0.8rem;
  color: #6b7280;
}

.membro-contato {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 15px;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 8px;
}

.contato-email {
  color: #2563eb;
  font-size: 0.9rem;
}

.membro-responsabilidades p {
  color: #6b7280;
  line-height: 1.5;
  margin: 0;
}

/* Cards de Orçamento */
.card-orcamento {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s;
}

.card-orcamento:hover {
  border-color: #10b981;
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.1);
}

.orcamento-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.orcamento-header h4 {
  margin: 0;
  color: #1f2937;
  font-size: 1.1rem;
}

.orcamento-tipo {
  background: #f3f4f6;
  color: #374151;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
}

.valores-orcamento {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.valor-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.valor-label {
  color: #6b7280;
  font-size: 0.9rem;
}

.valor-quantia.planejado {
  color: #059669;
  font-weight: 600;
}

.valor-quantia.executado {
  color: #dc2626;
  font-weight: 600;
}

.progresso-orcamento-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.barra-progresso-pequena {
  flex: 1;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}

.progresso-fill-pequena {
  height: 100%;
  background: linear-gradient(90deg, #10b981, #059669);
  border-radius: 3px;
  transition: width 0.3s;
}

.progresso-fill-pequena.metrica {
  background: linear-gradient(90deg, #8b5cf6, #7c3aed);
}

.percentual-pequeno {
  font-size: 0.8rem;
  color: #059669;
  font-weight: 600;
  min-width: 35px;
}

/* Cards de Métrica */
.card-metrica {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s;
}

.card-metrica:hover {
  border-color: #8b5cf6;
  box-shadow: 0 8px 25px rgba(139, 92, 246, 0.1);
}

.metrica-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.metrica-header h4 {
  margin: 0;
  color: #1f2937;
  font-size: 1.1rem;
}

.metrica-tipo {
  background: #f3f4f6;
  color: #374151;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.8rem;
}

.metrica-valores {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.valor-meta,
.valor-atual {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.valor-meta .label,
.valor-atual .label {
  color: #6b7280;
  font-size: 0.9rem;
}

.valor-meta .valor {
  color: #8b5cf6;
  font-weight: 600;
}

.valor-atual .valor {
  color: #1f2937;
  font-weight: 600;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1000;
  backdrop-filter: blur(8px);
}

.modal-caixa {
  background: white;
  border-radius: 16px;
  padding: 30px;
  width: min(500px, 100%);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-caixa h3 {
  margin: 0 0 15px 0;
  color: #1f2937;
  font-size: 1.5rem;
}

.modal-descricao {
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 25px;
}

.grupo-botoes {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.botao-texto {
  border: none;
  background: none;
  color: #6b7280;
  font-weight: 600;
  cursor: pointer;
  padding: 10px 16px;
}

.botao-primario.perigoso {
  background: #dc2626;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}

/* Estados de Loading e Erro */
.carregando {
  padding: 60px 20px;
  text-align: center;
  color: #6b7280;
  font-size: 1.1rem;
}

.erro {
  padding: 30px;
  text-align: center;
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  margin: 20px 0;
}

/* Responsividade */
@media (max-width: 1024px) {
  .grid-resumo {
    grid-template-columns: 1fr;
  }

  .grid-cards {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .container-projeto {
    padding: 15px;
  }

  .cabecalho-principal {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
  }

  .acoes-principais {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .titulo-projeto {
    font-size: 2rem;
  }

  .indicadores-rapidos {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  }

  .tabs-nav {
    flex-wrap: wrap;
  }

  .tab-button {
    flex: 1 1 auto;
    min-width: 0;
  }

  .grid-acoes {
    grid-template-columns: 1fr;
  }

  .secao-header {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }

  .estatisticas-rapidas {
    grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  }
}

@media (max-width: 480px) {
  .tab-content {
    padding: 20px;
  }

  .acao-rapida {
    flex-direction: column;
    text-align: center;
  }

  .modal-caixa {
    padding: 20px;
  }
}

/* Animações */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.detalhe-projeto {
  animation: fadeIn 0.6s ease-out;
}

/* Utilitários */
.situacao-ativo {
  background: #dcfce7;
  color: #166534;
}
.situacao-pausado {
  background: #fef3c7;
  color: #92400e;
}
.situacao-finalizado {
  background: #e0e7ff;
  color: #3730a3;
}
.situacao-cancelado {
  background: #fee2e2;
  color: #991b1b;
}

.saude-otima {
  background: #dcfce7;
  color: #166534;
}
.saude-boa {
  background: #fef3c7;
  color: #92400e;
}
.saude-atencao {
  background: #fed7aa;
  color: #9a3412;
}
.saude-critica {
  background: #fee2e2;
  color: #991b1b;
}

.situacao-concluida {
  background: #dcfce7;
  color: #166534;
}
.situacao-em-andamento {
  background: #dbeafe;
  color: #1d4ed8;
}
.situacao-atrasada {
  background: #fee2e2;
  color: #991b1b;
}
.situacao-planejada {
  background: #f3f4f6;
  color: #374151;
}
</style>
